import {
  faMagic,
  faMessages,
  faVideo,
} from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  JoonUIColor,
  Loader,
  SessionBookingLinkType,
  SPACING,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import { SelectRow } from "../../components/selectRow/SelectRow"
import { SelectRowContainer } from "../../components/selectRowContainer/SelectRowContainer"
import { useGetMeTabCardQuery } from "../../networking/queries"
import { MeTabCardType } from "../../types/coachingGroups"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { useRouter } from "../../util/router"
import { localStorageItems } from "../../util/storage"
import CareTeamAvatar from "../care/CareTeamAvatar"
import useCareTeam from "../care/useCareTeam"
import usePlanUpdateModalStore from "../coach/planUpdate/usePlanUpdateModalStore"
import { useSessionBookingModalStore } from "../coach/sessions/hooks/useSessionBookingModalStore"

const CoachAlertSection = () => {
  const { onOpen: openSessionBookingModal } = useSessionBookingModalStore()
  const { onOpen: onOpenPlanUpdateModal } = usePlanUpdateModalStore()
  const { data: meTabCard, isLoading } = useGetMeTabCardQuery()
  const { firstCoach } = useCareTeam()
  const router = useRouter()
  const { t } = useTranslation()
  const {
    card_type: cardType,
    next_session: nextSession,
    booking_link: bookingLink,
    unread_message: unreadMessage,
    num_session: numSessions,
  } = meTabCard || {}
  const isUnreadMessageState = cardType === MeTabCardType.UNREAD_MESSAGE
  const isBookSessionState = cardType === MeTabCardType.BOOK_SESSION
  const isViewNewRoutineState = cardType === MeTabCardType.VIEW_NEW_ROUTINE
  const isSessionBookedState = cardType === MeTabCardType.SESSION_BOOKED
  const isMessageState = cardType === MeTabCardType.MESSAGE

  const sessionIsWithin24Hours =
    dayjs(nextSession?.start_datetime).isBefore(dayjs().add(24, "hours")) &&
    dayjs(nextSession?.end_datetime).isAfter(dayjs())

  const onBookSession = () => {
    if (numSessions === 0) {
      trackAnalyticEvent(ANALYTIC_EVENTS.INITIATE_UPDATE_COACH_PLAN)
      return onOpenPlanUpdateModal()
    }
    if (!firstCoach?.user.id || !bookingLink) return
    localStorage.setItem(localStorageItems.needsToRefreshBookings, "true")
    openSessionBookingModal(bookingLink, SessionBookingLinkType.SCHEDULE)
    trackAnalyticEvent(ANALYTIC_EVENTS.SCHEDULE_COACH_SESSION)
    router.push(`/coach/${firstCoach.user.id}/sessions/`)
  }

  const onLaunchSession = () => {
    if (
      !sessionIsWithin24Hours ||
      nextSession?.marked_no_show ||
      !nextSession?.meeting_link
    )
      return

    trackAnalyticEvent(ANALYTIC_EVENTS.JOIN_COACHING_SESSION)
    window.open(nextSession.meeting_link, "_blank")
  }

  const onSessionTopicClick = () => {
    if (!firstCoach?.user.id || !nextSession) return
    trackAnalyticEvent(ANALYTIC_EVENTS.ADD_SESSION_TOPIC)
    router.push(`/coach/${firstCoach.user.id}/sessions/${nextSession.id}`)
  }

  const onChatClick = () => {
    if (!firstCoach?.user.id) return
    trackAnalyticEvent(ANALYTIC_EVENTS.VIEW_COACHING_MESSAGES)
    router.push(`/coach/${firstCoach.user.id}/messages`)
  }

  const onRoutineClick = () => {
    if (!firstCoach?.user.id) return
    trackAnalyticEvent(ANALYTIC_EVENTS.VIEW_NEW_COACH_ROUTINE)
    router.push(`/coach/${firstCoach.user.id}/routine`)
  }

  const isSessionBookedUnreadMessageOrRoutine =
    isSessionBookedState || isUnreadMessageState || isViewNewRoutineState

  return (
    <SelectRowContainer>
      <SelectRow
        hideBorder
        showBorderBottom={isSessionBookedUnreadMessageOrRoutine}
        style={{ padding: SPACING.space2 }}
      >
        <div className="flex flex-row justify-start items-center gap-2 p-2">
          <div className="relative">
            {firstCoach && <CareTeamAvatar profile={firstCoach} size="sm" />}
            <div className="absolute bottom-0 -right-[1px] w-2 h-2 rounded-full bg-viridian-300" />
          </div>
          <Typography variant="h4" textAlign="left">
            {t("ME_COACH_NAME", { name: firstCoach?.user.name })}
          </Typography>
        </div>
        <RightElement />
      </SelectRow>
      {isBookSessionState ? (
        <div className="w-full flex flex-col p-4 pt-0 gap-3">
          <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
            {t("ME_COACH_NO_SESSION")}
          </Typography>
          <Button
            onClick={onBookSession}
            text={t("ME_COACH_BOOK_SESSION")}
            buttonType="primary"
            fullWidth
          />
        </div>
      ) : (
        isMessageState && (
          <div className="w-full flex flex-col p-4 pt-0 gap-3">
            <Button
              onClick={onChatClick}
              text={t("ME_COACH_CHAT")}
              buttonType="primary"
              fullWidth
            />
          </div>
        )
      )}
      {isLoading ? (
        <div className="w-full flex justify-center items-center pb-2">
          <Loader size={16} thickness={2} color={JoonUIColor.text.secondary} />
        </div>
      ) : (
        isSessionBookedUnreadMessageOrRoutine && (
          <SelectRow hideBorder>
            <div className="w-full flex flex-col gap-2 justify-start items-start">
              {!isSessionBookedState ? (
                <>
                  <div className="w-full flex flex-row justify-start items-center gap-2 px-1">
                    {" "}
                    <div className="w-2 h-2 rounded-full bg-error outline outline-2 outline-red-100" />
                    <Typography variant="body" textAlign="left">
                      {isUnreadMessageState
                        ? t("ME_COACH_NEW_MESSAGE", {
                            name: firstCoach?.user.name,
                          })
                        : t("ME_COACH_NEW_ROUTINES", {
                            name: firstCoach?.user.name,
                          })}
                    </Typography>
                  </div>
                  {isUnreadMessageState ? (
                    <div className="w-full flex flex-col justify-start items-start px-0 pt-2 bg-bgLightGray rounded-md">
                      <Typography
                        variant="bodySmall"
                        style={{
                          width: "100%",
                          textAlign: "left",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          whiteSpace: "pre-line",
                          display: "-webkit-box",
                          paddingLeft: SPACING.space2,
                          paddingRight: SPACING.space2,
                        }}
                      >
                        {unreadMessage?.message}
                      </Typography>
                      <TextButton
                        onClick={onChatClick}
                        style={{ padding: SPACING.space2 }}
                      >
                        <Typography
                          variant="bodyXSmall"
                          color={JoonUIColor.text.primaryAccent}
                        >
                          {t("ME_COACH_READ_MESSAGE")}
                        </Typography>
                      </TextButton>
                    </div>
                  ) : (
                    <Button
                      text={t("ME_COACH_REVEAL_ROUTINE")}
                      buttonType="primary"
                      onClick={() => {
                        localStorage.setItem(
                          localStorageItems.lastSeenNewRoutinesOverlay,
                          new Date().toISOString()
                        )
                        onRoutineClick()
                      }}
                      fullWidth
                      icon={faMagic}
                    />
                  )}
                </>
              ) : (
                <div className="w-full flex flex-col items-start gap-3 px-1">
                  <div className="w-full flex flex-col items-start">
                    <Typography
                      variant="bodySmall"
                      color={JoonUIColor.text.secondary}
                    >
                      {t("ME_COACH_NEXT_SESSION")}
                    </Typography>
                    <Typography variant="bodySmall">
                      {dayjs(nextSession?.start_datetime).format(
                        "dddd, MMM D, h:mm a"
                      )}
                    </Typography>
                  </div>
                  <div className="w-full flex flex-col gap-2">
                    {sessionIsWithin24Hours && !nextSession?.marked_no_show && (
                      <Button
                        text={t("ME_COACH_LAUNCH_SESSION")}
                        fullWidth
                        onClick={onLaunchSession}
                      />
                    )}
                    <Button
                      text={t("ME_COACH_SESSION_TOPIC")}
                      buttonType="secondary"
                      fullWidth
                      onClick={onSessionTopicClick}
                    />
                  </div>
                </div>
              )}
            </div>
          </SelectRow>
        )
      )}
    </SelectRowContainer>
  )
}

const RightElement = () => {
  const { data: meTabCard } = useGetMeTabCardQuery()
  const { onOpen: onOpenPlanUpdateModal } = usePlanUpdateModalStore()
  const { onOpen: openSessionBookingModal } = useSessionBookingModalStore()
  const { firstCoach } = useCareTeam()
  const router = useRouter()
  const { t } = useTranslation()

  const {
    card_type: cardType,
    booking_link: bookingLink,
    num_session: numSessions,
  } = meTabCard || {}

  const isBookSessionState = cardType === MeTabCardType.BOOK_SESSION
  const isMessageState = cardType === MeTabCardType.MESSAGE

  const onBookSession = () => {
    if (numSessions === 0) {
      trackAnalyticEvent(ANALYTIC_EVENTS.INITIATE_UPDATE_COACH_PLAN)
      return onOpenPlanUpdateModal()
    }
    if (!firstCoach?.user.id || !bookingLink) return
    localStorage.setItem(localStorageItems.needsToRefreshBookings, "true")
    openSessionBookingModal(bookingLink, SessionBookingLinkType.SCHEDULE)
    router.push(`/coach/${firstCoach.user.id}/sessions/`)
  }

  const onChatClick = () => {
    if (!firstCoach?.user.id) return
    trackAnalyticEvent(ANALYTIC_EVENTS.VIEW_COACHING_MESSAGES)
    router.push(`/coach/${firstCoach.user.id}/messages`)
  }

  if (isMessageState)
    return (
      <div className="flex flex-row justify-end items-center">
        <TextButton onClick={onBookSession}>
          <div className="flex flex-row items-center gap-1">
            <FontAwesomeIcon
              icon={faVideo}
              fontSize={15}
              color={JoonUIColor.icon.accent}
            />
            <Typography variant="bodySmall">
              {t("ME_COACH_BOOK_A_SESSION")}
            </Typography>
          </div>
        </TextButton>
      </div>
    )

  if (isBookSessionState)
    return (
      <div className="flex flex-row justify-end items-center">
        <TextButton onClick={onChatClick}>
          <div className="flex flex-row items-center gap-1">
            <FontAwesomeIcon
              icon={faMessages}
              fontSize={15}
              color={JoonUIColor.icon.accent}
            />
            <Typography variant="bodySmall">{t("ME_COACH_CHAT")}</Typography>
          </div>
        </TextButton>
      </div>
    )

  return (
    <div className="flex flex-row justify-end items-center">
      <TextButton onClick={onBookSession}>
        <div className="flex flex-row items-center gap-2">
          <FontAwesomeIcon
            icon={faVideo}
            fontSize={15}
            color={JoonUIColor.icon.accent}
          />
          <Typography variant="bodySmall">{t("ME_COACH_BOOK")}</Typography>
        </div>
      </TextButton>
      <div
        style={{
          width: "1px",
          height: "16px",
          backgroundColor: JoonUIColor.border.default,
        }}
      />
      <TextButton onClick={onChatClick}>
        <div className="flex flex-row items-center gap-2">
          <FontAwesomeIcon
            icon={faMessages}
            fontSize={15}
            color={JoonUIColor.icon.accent}
          />
          <Typography variant="bodySmall">Chat</Typography>
        </div>
      </TextButton>
    </div>
  )
}

export default CoachAlertSection
